<template>
  <div class="main-desktop" :class="mainClasses">
    <MainWrapper>
      <template
        #default="{
          isEmptyApp,
          isEmptyAnalytics,
          isEmptyConnectors,
          isWelcomeScreenVisible,
        }"
      >
        <MainDesktopHeader :disabled="isEmptyApp" />
        <div class="main-desktop__container">
          <MainDesktopNavbar
            v-if="!isEmptyApp"
            :disabled="isEditMode"
            :items="navbarItems"
          >
            <slot name="navbar" />
            <template #footer>
              <slot name="navbar-footer" />
            </template>
            <template #after>
              <slot name="navbar-after" />
            </template>
          </MainDesktopNavbar>
          <main class="main-desktop__content">
            <slot name="content-before" />
            <AppWelcomeScreen
              v-if="isWelcomeScreenVisible"
              :is-empty-app
              :is-empty-analytics
              :is-empty-connectors
            />
            <template v-else>
              <slot name="content" :is-tabs-visible :is-edit-mode />
              <router-view v-slot="{ Component }">
                <component :is="Component" />
              </router-view>
            </template>
          </main>
        </div>
        <AppModalTray />
      </template>
    </MainWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useRoute } from 'vue-router'

import { NavigationItem } from '@types'

import { NAVBAR_OPENED } from '../utils/const'

import { useDashboardsStore } from '@/store/dashboards'

import MainDesktopHeader from './Header/Header.vue'
import MainDesktopNavbar from './Navbar/Navbar.vue'
import MainWrapper from '../components/Wrapper.vue'

import { AppWelcomeScreen, AppModalTray } from '@app'

type Props = {
  navbarItems?: NavigationItem[]
}

defineProps<Props>()

const route = useRoute()

const dashboardsStore = useDashboardsStore()

const isNavbarOpened = useLocalStorage<boolean>('NAVBAR_OPENED', false)

provide(NAVBAR_OPENED, isNavbarOpened)

const isEditMode = computed(() => dashboardsStore.getEditMode)

const isTabsVisible = computed(() => !route.meta.hideTabs)

const mainClasses = computed(() => ({
  'main-desktop--opened-navbar': isNavbarOpened.value,
}))
</script>

<style scoped>
.main-desktop {
  @apply h-dvh flex flex-col;
  @apply md:overflow-hidden;

  &__container {
    @apply flex flex-auto flex-row;
    @apply md:overflow-hidden;
  }

  &__content {
    @apply w-full md:w-auto;
    @apply relative;
    @apply flex flex-col flex-auto;
    @apply px-default pb-0;
    @apply md:overflow-auto;
    @apply transition-all;
  }
}
</style>
